import { useTranslation } from "@sagot/i18n";
import { Button, Field, Input, Switch, toast } from "@sagot/ui";
import { useId, useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { GLOSSARY_LIMITS, capProblem } from "./glossary-model";
import { SettingsCard } from "./settings-ui";
import { useSettingsMutations } from "./use-agent-settings";

const DEFAULT_CAP = 120;

interface ReplyCapCardProps {
  agentId: string;
  /** The saved cap in words; `null` = none. */
  cap: number | null;
  canEdit: boolean;
}

/**
 * The agent's cap on a reply, saved on its own (`PATCH /v1/agents/{id}` with `behavior.reply_max_words`). It sits inside the
 * agent settings form, so it is not a form itself: Enter saves the cap and never submits the page.
 */
export function ReplyCapCard({ agentId, cap, canEdit }: ReplyCapCardProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const { update } = useSettingsMutations(agentId);
  const switchId = useId();
  const [on, setOn] = useState(cap !== null);
  const [words, setWords] = useState(String(cap ?? DEFAULT_CAP));
  const [touched, setTouched] = useState(false);
  const invalid = on && capProblem(words);
  const unchanged = on ? cap === Number(words) : cap === null;

  const save = () => {
    setTouched(true);
    if (invalid) return;
    update.mutate(
      { behavior: { reply_max_words: on ? Number(words) : null } },
      {
        onSuccess: () => { toast.success(t(on ? "glossary.tab.capSaved" : "glossary.tab.capRemoved")); },
        onError: (error) => { toast.error(message(error)); },
      },
    );
  };

  return (
    <SettingsCard title={t("glossary.tab.capTitle")} hint={t("glossary.tab.capHint")}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <label id={`${switchId}-name`} htmlFor={switchId} className="text-sm font-semibold">{t("glossary.tab.capSwitch")}</label>
          <p id={`${switchId}-hint`} className="mt-0.5 text-xs text-fg-subtle">{t("glossary.tab.capSwitchHint")}</p>
        </div>
        <Switch id={switchId} aria-labelledby={`${switchId}-name`} aria-describedby={`${switchId}-hint`} className="mt-0.5 shrink-0" checked={on} disabled={!canEdit} onCheckedChange={setOn} />
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        {on ? (
          <Field
            label={t("glossary.tab.capWords")}
            className="sm:w-56"
            error={touched && invalid ? t("glossary.tab.capRange", { min: GLOSSARY_LIMITS.replyMin, max: GLOSSARY_LIMITS.replyMax }) : null}
          >
            <Input
              type="number"
              inputMode="numeric"
              min={GLOSSARY_LIMITS.replyMin}
              max={GLOSSARY_LIMITS.replyMax}
              value={words}
              disabled={!canEdit}
              invalid={touched && invalid}
              onChange={(event) => { setWords(event.target.value); }}
              onBlur={() => { setTouched(true); }}
              onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); save(); } }}
            />
          </Field>
        ) : null}
        {canEdit ? <Button type="button" loading={update.isPending} disabled={unchanged} onClick={save}>{t("glossary.tab.capSave")}</Button> : null}
      </div>
      <p className="text-xs text-fg-muted" role="status">{cap === null ? t("glossary.tab.capNone") : t("glossary.tab.capCurrent", { count: cap })}</p>
    </SettingsCard>
  );
}
